import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import BottomSheetSelect from '@/components/molecules/BottomSheetSelect';
import { usePreferenceStore } from '@/stores/usePreferenceStore';
import { SUPPORTED_CURRENCIES, SUPPORTED_LOCALES } from '@/types/common';
import {
  ensureLocaleTranslations,
  ensureLocaleEntryTranslations,
} from '@/i18n';

const prepareLanguages = () => ensureLocaleTranslations('zh-CN');
const prepareChineseEntry = () => ensureLocaleEntryTranslations('zh-CN');

const triggerClass =
  'h-11 min-w-11 gap-0.5 rounded-full border-gp-ink-900/10 bg-white px-2.5 text-caption font-semibold max-[360px]:gap-0 max-[360px]:px-1';

/** Compact customer header triggers reuse the accessible shared selection sheet. */
export function LanguageSheet() {
  const { t } = useTranslation();
  const locale = usePreferenceStore((state) => state.locale ?? 'en');
  const setLocale = usePreferenceStore((state) => state.setLocale);
  return (
    <BottomSheetSelect
      label={t('header.language')}
      description={t('header.language')}
      value={locale}
      options={SUPPORTED_LOCALES.map((value) => ({
        value,
        label: t(`language.${value}`),
      }))}
      onSelect={setLocale}
      prepare={locale === 'zh-CN' ? prepareChineseEntry : prepareLanguages}
      triggerAriaLabel={t('header.languageValue', {
        value: locale === 'zh-CN' ? '中文' : 'EN',
      })}
      triggerClassName={triggerClass}
      triggerContent={
        <>
          {locale === 'zh-CN' ? '中文' : 'EN'}
          <ChevronDown className="size-3.5" aria-hidden="true" />
        </>
      }
    />
  );
}

/** Amounts are still server display values; changing currency only changes preference/query. */
export function CurrencySheet() {
  const { t } = useTranslation();
  const currency = usePreferenceStore((state) => state.currency);
  const setCurrency = usePreferenceStore((state) => state.setCurrency);
  return (
    <BottomSheetSelect
      label={t('header.currency')}
      description={t('header.currency')}
      value={currency}
      options={SUPPORTED_CURRENCIES.map((value) => ({
        value,
        label: t(`currency.${value}`),
      }))}
      onSelect={setCurrency}
      triggerAriaLabel={t('header.currencyValue', { value: currency })}
      triggerClassName={triggerClass}
      triggerContent={
        <>
          {currency}
          <ChevronDown className="size-3.5" aria-hidden="true" />
        </>
      }
    />
  );
}
